
import CTNAutocomplete from '@ctn/ui/components/ctn-autocomplete';
import CTNButton from '@ctn/ui/components/ctn-button';
import CTNDropdown from '@ctn/ui/components/ctn-dropdown';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNMessage from '@ctn/ui/components/ctn-message';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNPagination from '@ctn/ui/components/ctn-pagination';
import CTNSpinner from '@ctn/ui/components/ctn-spinner';
import { type CTNAutocompleteOptionProps } from '@ctn/ui/types/ctn-autocomplete';
import { type CTNButtonIconProps } from '@ctn/ui/types/ctn-button';
import { type CTNDropdownOptionProps } from '@ctn/ui/types/ctn-dropdown';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useMapContext } from '@/context/Maps/mapComponent.context';
import { getMunicipios, getNotarios } from '@/services/maestros.service';

import { notificationsConfig } from '@/helpers/notifications';
import { type IMunicipiosBDCorpo } from '@/interfaces/model/maestros.interface';
import { type INotaria } from '@/interfaces/model/TramiteNuevo/INotaria.interface';
import { type IOrderOption } from '@/interfaces/model/TramiteNuevo/ISearchNotaryModal.interface';

import NotaryData from '../components/NotaryData';

type SearchNotaryViewProps = {
	readonly foundNotaries: INotaria[];
	readonly setFoundNotaries: (foundNotaries: INotaria[]) => void;
	readonly totalResults: number | undefined;
	readonly setTotalResults: (totalResults: number) => void;
	readonly page: number;
	readonly setPage: (page: number) => void;
	readonly setSelectedNotary: (notary: INotaria) => void;
	readonly errorMessage: string;
};

const LIMIT = 3;

type SearchFormValues = {
	municipio: {label: string; value: string} | undefined;
	nombre: string;
	apellido: string;
	orderBy?: IOrderOption;
};

const SearchNotaryView = ({
	foundNotaries,
	setFoundNotaries,
	totalResults,
	setTotalResults,
	page,
	setPage,
	setSelectedNotary,
	errorMessage,
}: SearchNotaryViewProps) => {
	const {
		state: { notariosLocations, userLocation },
	} = useMapContext();
	const methods = useForm<SearchFormValues>({
		defaultValues: {
			// @ts-expect-error autocomplete needs null
			municipio: null,
			nombre: '',
			apellido: '',
		}
	});
	const { openNotification, closeNotification } = useNotification();
	const configuracionTemplate = notificationsConfig({ closeNotification });
	const [municipios, setMunicipios] = useState<CTNAutocompleteOptionProps[]>();
	const [municipiosFiltered, setMunicipiosFiltered] = useState<CTNAutocompleteOptionProps[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [autocompleteValue, setAutocompleteValue] = useState<string>('');

	const normalizarPalabra = (palabra: string) =>
		palabra
			?.normalize('NFD') // Normalizar caracteres (tildes)
			?.replace(/[\u0300-\u036f]/g, '') // Eliminar tildes
			?.replace(/[^\w\s]/gi, '') // Eliminar caracteres especiales
			?.toLowerCase(); 

	const ORDER_OPTIONS = [
		{
			label: 'Apellidos',
			value: 'apellidos',
		},
	];
	if (userLocation.userLat !== 0 && userLocation.userLng !== 0) {
		ORDER_OPTIONS.push({
			label: 'Distancia',
			value: 'distancia',
		});
	}

	const watchNombre = methods.watch('nombre');
	const watchApellido = methods.watch('apellido');

	useEffect(() => {
		const asyncMethod = async () => {
			const response = await getMunicipios();
			setMunicipios(response.data.map((m: IMunicipiosBDCorpo) => ({ label: String(m.nombre), value: `${String(m.codigoProvincia)};${m.codigo}` })	
			));
		};
		asyncMethod();
	}, []); 

	const filterByApellidos = () => {
		const listSorted = [...foundNotaries].sort((a, b) => {
			if (a.notarioApellidos.toUpperCase() < b.notarioApellidos.toUpperCase()) {
				return -1;
			}
			if (a.notarioApellidos.toUpperCase() > b.notarioApellidos.toUpperCase()) {
				return 1;
			}
			return 0;
		});
		setFoundNotaries(listSorted);
	};

	const listWithDistancia = () => [...foundNotaries].map((notario) => {
		const coincidence = notariosLocations.find(
			(location) => location.codigoNotarioNotaria === notario.codigoNotarioNotaria,
		);
		if (coincidence) {
			return { ...notario, distancia: coincidence.distance };
		}
		return notario;
	});

	const orderNotariesList = (value: string) => {
		if (value === 'apellidos') {
			filterByApellidos();
			return;
		}
		const distancia = listWithDistancia();
		// Sacar del array los notarios que no tienen ubicación, para después añadirlos al final del array ordenado
		const notariesWithoutLocation = [...distancia].filter((not) => !not.distancia);
		const listSorted = [...distancia]
			.filter((not) => not.distancia)
			.sort((a, b) => {
				if (a.distancia && b.distancia) {
					return Math.sign(a.distancia - b.distancia);
				}
				return 0;
			});
		setFoundNotaries([...listSorted, ...notariesWithoutLocation]);
	};
	
	const filterMunicipios = ({ query }: { query: string }) => {
		let filteredData: CTNAutocompleteOptionProps[] = [];
		if ((query || '').trim().length > 0) {
			const palabraFiltrar = normalizarPalabra(query ?? '');
			filteredData = (municipios ?? [])
				?.filter((m: CTNAutocompleteOptionProps) => normalizarPalabra(m.label).includes(palabraFiltrar.toLowerCase()))
				.sort((a, b) => (b.label.startsWith(palabraFiltrar) ? 1 : -1))
				.slice(0, 200);
		}
		setMunicipiosFiltered(filteredData ?? []);
	};

	const getSelectedValues = (): { codigoPostal: string; municipio: string; provincia: string } => {
		let value = autocompleteValue;
		const isNumeric = /^\d+$/.test(value);
		const codigoPostal = isNumeric ? value : '';

		if (!autocompleteValue.includes(';') && !isNumeric) {
			value = municipiosFiltered[0].value.toString();
		}
		const provincia = isNumeric ? '' : value.split(';')[0];
		const municipio = isNumeric ? '' : value.split(';')[1];
		return { codigoPostal, municipio, provincia };
	};

	const showErrorOnGetNotaries = () => {
		openNotification(
			configuracionTemplate.adviceMessage(
				<>
					<p>
						<b>No se han encontrado resultados con el criterio de búsqueda actual</b>
					</p>
					<p className="u-mt-16">
						Debe seleccionar un municipio de la lista.
					</p>
				</>,
			),
		);
	};

	const getNotaries = async () => {
		setIsLoading(true);
		setFoundNotaries([]);

		try {
			const { codigoPostal, municipio, provincia } = getSelectedValues();
			const { code, data} = await getNotarios(codigoPostal, municipio, provincia, watchNombre, watchApellido);
			setIsLoading(false);
			if (code === 200) {
				setFoundNotaries(data.listado);
				setTotalResults(data.totalResults ?? 0);
				setPage(1);
			} else {
				setFoundNotaries([]);
				setTotalResults(0);
				showErrorOnGetNotaries();
			}
		} catch (error: any) {
			setIsLoading(false);
			showErrorOnGetNotaries();
		}
	};

	const resetValues = () => {
		// @ts-expect-error autocomplete needs null
		methods.setValue('municipio', null);
		methods.setValue('nombre', '');
		methods.setValue('apellido', '');
		methods.setValue('orderBy', undefined);
		setAutocompleteValue('');
	};

	const getNoResultsMessage = (message: string): ReactNode => (
		<div className="u-mt-16">
			<p className="c-pnp-search-notary__no-result-title">
				No se han obtenido resultados
			</p>
			<div className="[ c-pnp-search-notary__empty ] [ u-mt-16 ]">
				<p className="c-pnp-search-notary__message ">
					{message}
				</p>
			</div>
		</div>
	);

	const renderDropdownOrdrBy = (): ReactNode => {
		if (!foundNotaries || foundNotaries.length === 0) {
			return null;
		}
		return (
			<div className="c-pnp-search-notary__order">
				<CTNDropdown
					control={methods.control}
					error=""
					placeholder="Ordenar por"
					options={ORDER_OPTIONS}
					name="orderBy"
					onChange={(e: CTNDropdownOptionProps | undefined | CTNDropdownOptionProps[]) => {
						const val = Array.isArray(e) 
							? (e[0]?.value ?? '') 
							: (e?.value ?? '');

						orderNotariesList(String(val));
					}}
				/>
			</div>
		);
	};

	const renderFoundNotaries = () => {
		if (totalResults === undefined) {
			return getNoResultsMessage('Aún no has realizado la búsqueda del notario');
		}

		if (totalResults === 0) {
			return getNoResultsMessage('No se han encontrado resultados con los criterios indicados');
		}

		return (
			<div className="[ c-pnp-search-notary__results ]">
				<div className="[ c-pnp-search-notary__form-results ] [ u-mt-24 ]">
					<p className="c-pnp-search-notary__counter">
						{totalResults} notarios encontrados
					</p>
					{renderDropdownOrdrBy()}
				</div>
				<div className="[ c-pnm-search-notary__found-notaries__notaries-container ]">
					{[...foundNotaries].slice(page * LIMIT - LIMIT, page * LIMIT).map((not) => (
						<div
							key={not.codigoNotarioNotaria}
							className={`c-pnp-search-notary__notary-list ${totalResults > LIMIT ? 'c-pnp-search-notary__notary-list--pagination' : ''}`}
						>
							<NotaryData key={not.codigoNotarioNotaria} isShowMap data={not} />
							<CTNButton
								className="c-pnp-search-notary__btn"
								mode="secondary"
								label="Seleccionar notario"
								size="lg"
								onClick={() => {
									setSelectedNotary(not);
								}}
							/>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderPagination = () => {
		if (totalResults && totalResults > LIMIT) {
			return (
				<CTNPagination
					pageSize={LIMIT}
					totalData={totalResults}
					tablePagination={{
						pageIndex: page - 1,
						getCanNextPage: () => page < Math.ceil(totalResults / LIMIT),
						getCanPreviousPage: () => page > 1,
						setPageIndex: (index: number) => setPage(index + 1)
					}}
					locales={{
						text1: 'Página',
						text2: 'de',
						text3: 'registros'
					}}
					onPageChange={(newPage: number) => setPage(newPage)}
				/>
			);
		}
	};

	const icon: CTNButtonIconProps[] = [{
		icon: 'clean',
		color: 'blue-50'
	}];

	const debouncedFilter = useDebouncedCallback((query: string) => {
		setAutocompleteValue(query);
		filterMunicipios({ query });
	}, 300);

	const handleAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value} = e.target;
		debouncedFilter(value);
	};

	const handleAutocompleteSelect = (e: CTNAutocompleteOptionProps) => {
		setAutocompleteValue(e.value as string);
	};

	const handleAutocompleteReset = () => {
		setAutocompleteValue('');
		setMunicipiosFiltered([]);
	};

	const searchHtml = (): ReactNode => (
		<div className="[ c-pnp-search-notary__filter ] [ u-mt-24 ]">
			<div className="c-pnp-search-notary__field">
				<CTNAutocomplete
					required
					name="municipio"
					label="CÓDIGO POSTAL O MUNICIPIO"
					control={methods.control}
					noResultsLabel="No se han obtenido resultados"
					suggestions={municipiosFiltered}
					onChange={handleAutocompleteChange}
					onSelect={handleAutocompleteSelect}
					onClickReset={handleAutocompleteReset}
				/>
			</div>
			<div className="c-pnp-search-notary__field">
				<CTNInput
					name="nombre"
					label="Nombre del notario"
					control={methods.control}
					type="text"
				/>
			</div>
			<div className="c-pnp-search-notary__field">
				<CTNInput
					name="apellido"
					label="Nombre del apellido"
					control={methods.control}
					type="text"
				/>
			</div>
			<div className="c-pnp-search-notary__filter-buttons">
				<CTNButton
					className="u-mt-8"
					size="lg"
					label='Reiniciar filtros'
					mode="secondary"
					variant="ghost"
					icon={icon}
					type="button"
					onClick={resetValues}
				/>
				<CTNButton
					disabled={!autocompleteValue}
					className="[ u-mt-8 ]"
					type="button"
					label="Buscar"
					size="lg"
					onClick={getNotaries}
				/>
			</div>
		</div>
	);

	return (
		<div className="[ c-pnp-search-notary__view ]">
			<p className="[ c-pnp-search-notary__search-title ]">Selecciona uno o mas criterios de búsqueda</p>
			{searchHtml()}
			{errorMessage && (
				<CTNMessage
					className="[ c-pnp-search-notary__error-message ]"
					closable={false}
					status="error"
					content={errorMessage}
				/>
			)}
			{isLoading && (
				<div className="[ c-pnp-search-notary__spinner ]">
					<CTNSpinner size="xl" />
					<p>Buscando notarios...</p>
				</div>
			)}
			{!isLoading && renderFoundNotaries()}
			{!isLoading && renderPagination()}
		</div>
	);
};

export default SearchNotaryView;
